import { AnimatePresence } from 'framer-motion';
import { FC, useCallback, useState } from 'react';

import Button from '@/components/Button';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { checkIfWindows } from '@/utils/helpers';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  ImagesBox,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';
import VariationsStyles from './VariationsStyles';
import VariationsStylesWindows from './VariationsStylesWindows';

export interface ImageVariation {
  leftImageSrc: string;
  rightImageSrc: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  rightWatermarkTag: string;
  rightWatermarkPosition: string;
  leftText: string;
  rightText: string;
  leftTextPositon: string;
  leftQuotes: boolean;
  leftBreakes: boolean;
  rightTextPositon: string;
  rightQuotes: boolean;
  rightBreakes: boolean;
  divider: string;
}

interface Props {
  inArticle?: boolean;
}

const VariationLRFulltext: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [rightImages, setRightImages] = useState<ImageData[]>([]);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();
  const [showLeftImagesModal, setShowLeftImagesModal] = useState(false);
  const [showRightImagesModal, setShowRightImagesModal] = useState(false);

  const renderVariationStyles = () => {
    if (checkIfWindows()) {
      return <VariationsStylesWindows />;
    }
    return <VariationsStyles />;
  };

  const generateVariations = useCallback(() => {
    if (!leftImages.length || !rightImages.length) return;

    const newVariations = leftImages.flatMap((leftImage) =>
      rightImages.map((rightImage) => ({
        leftImageSrc:
          leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
        rightImageSrc:
          rightImage.croppedImage ?? URL.createObjectURL(rightImage.image),
        leftWatermarkTag: getWatermarkTag(leftImage),
        leftWatermarkPosition: getWatermarkPosition(leftImage),
        rightWatermarkTag: getWatermarkTag(rightImage),
        rightWatermarkPosition: getWatermarkPosition(rightImage),
        leftText: leftImage.text,
        rightText: rightImage.text,
        leftTextPositon: leftImage.textPosition,
        leftQuotes: leftImage.quotes,
        leftBreakes: leftImage.brakes,
        rightTextPositon: rightImage.textPosition,
        rightQuotes: rightImage.quotes,
        rightBreakes: rightImage.brakes,
        divider: leftImage.divider,
      })),
    );

    setVariations(newVariations);
  }, [leftImages, rightImages]);

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      settings
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
      type="left"
    />
  ));

  const renderRightImages = rightImages.map((image, index) => (
    <UploadedImageRow
      settings
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setRightImages}
      type="right"
    />
  ));

  const onReset = () => {
    setRightImages([]);
    setLeftImages([]);
    setVariations([]);
    setImages([]);
  };

  return (
    <>
      {renderVariationStyles()}
      <Box>
        <Header>
          <div>
            <Heading $type="button">L/R + Full text</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach 2 images for left and right side, add text and set needed
              parameters to get results
            </Text>
          </div>
          <HeaderButtons>
            <ResetButton
              color="red"
              onClick={onReset}
              disabled={!leftImages?.length && !rightImages?.length}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <div>
            <ImagesBox $hasImages={!!leftImages?.length}>
              <LabelText $type="smallHeadline">Left Images</LabelText>
              {!!leftImages?.length ? (
                <>
                  <UploadedImages>{renderLeftImages}</UploadedImages>
                  <MoreImages>
                    <AttachedCountBadge count={leftImages.length} />
                    <AddMoreButton
                      onClick={() => setShowLeftImagesModal(true)}
                      color="colorized"
                    >
                      Add more images
                    </AddMoreButton>
                  </MoreImages>
                </>
              ) : (
                <UploadMultipleImages
                  blackDivider
                  coverIndex={1}
                  setImages={setLeftImages}
                />
              )}
            </ImagesBox>
            <ImagesBox $hasImages={!!rightImages?.length}>
              <LabelText $type="smallHeadline">Right Images</LabelText>
              {!!rightImages?.length ? (
                <>
                  <UploadedImages>{renderRightImages}</UploadedImages>
                  <MoreImages>
                    <AttachedCountBadge count={rightImages.length} />
                    <AddMoreButton
                      onClick={() => setShowRightImagesModal(true)}
                      color="colorized"
                    >
                      Add more images
                    </AddMoreButton>
                  </MoreImages>
                </>
              ) : (
                <UploadMultipleImages
                  blackDivider
                  coverIndex={2}
                  setImages={setRightImages}
                />
              )}
            </ImagesBox>
          </div>
        )}
      </Box>
      <Filler />
      {showContent && (
        <ButtonHolder>
          <Button
            disabled={!leftImages?.length || !rightImages?.length}
            onClick={generateVariations}
          >
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showLeftImagesModal && (
          <MoreImagesModal
            onClose={() => setShowLeftImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
            blackDivider
          />
        )}
        {!!showRightImagesModal && (
          <MoreImagesModal
            onClose={() => setShowRightImagesModal(false)}
            setImages={setRightImages}
            coverIndex={2}
            blackDivider
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationLRFulltext;
